import type {
  CycleMonitoringDecision,
  CycleMonitoringEncounter,
} from "@src/packages/care/hooks/useCycleMonitoring";
import type { Cycle } from "@src/packages/cycles/hooks/useCycles";

export type CycleMonitoringProgressionDecision = Extract<
  CycleMonitoringDecision["decision"],
  "trigger" | "proceed_iui" | "proceed_timed_intercourse"
>;

export type CycleMonitoringDecisionReviewState =
  "none" | "awaiting_review" | "instruction_recorded" | "not_proceeding";

export type CycleMonitoringDecisionState = {
  reviewState: CycleMonitoringDecisionReviewState;
  latestActiveDecision: CycleMonitoringDecision | null;
  latestPendingEncounter: CycleMonitoringEncounter | null;
  allows: (decision: CycleMonitoringProgressionDecision) => boolean;
};

const BLOCKING_DECISIONS = new Set([
  "cancel",
  "withhold_intercourse_or_insemination",
]);

function timestamp(value: string | null | undefined): number {
  if (!value) return 0;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
}

function latestDecision(
  decisions: readonly CycleMonitoringDecision[],
): CycleMonitoringDecision | null {
  return (
    [...decisions]
      .filter((decision) => decision.status === "active")
      .sort(
        (left, right) =>
          timestamp(right.effectiveAt) - timestamp(left.effectiveAt) ||
          timestamp(right.issuedAt) - timestamp(left.issuedAt) ||
          right.id.localeCompare(left.id),
      )[0] ?? null
  );
}

function latestPendingEncounter(
  encounters: readonly CycleMonitoringEncounter[],
): CycleMonitoringEncounter | null {
  return (
    [...encounters]
      .filter(
        (encounter) =>
          encounter.status !== "voided" &&
          (encounter.status === "awaiting_review" ||
            encounter.status === "results_partial" ||
            encounter.status === "results_complete" ||
            (encounter.status === "occurred" &&
              encounter.reviewStatus === "pending" &&
              encounter.measurements.length > 0)),
      )
      .sort(
        (left, right) =>
          timestamp(right.actualAt ?? right.scheduledAt) -
            timestamp(left.actualAt ?? left.scheduledAt) ||
          right.id.localeCompare(left.id),
      )[0] ?? null
  );
}

export function resolveCycleMonitoringDecisionState(
  decisions: readonly CycleMonitoringDecision[] | undefined,
  encounters: readonly CycleMonitoringEncounter[] | undefined,
  now = new Date(),
): CycleMonitoringDecisionState {
  const effectiveDecisions = (decisions ?? []).filter(
    (decision) => timestamp(decision.effectiveAt) <= now.getTime(),
  );
  const latestActiveDecision = latestDecision(effectiveDecisions);
  const pendingEncounter = latestPendingEncounter(encounters ?? []);
  const decisionTime = timestamp(
    latestActiveDecision?.effectiveAt ?? latestActiveDecision?.issuedAt,
  );
  const pendingAfterDecision =
    pendingEncounter !== null &&
    timestamp(pendingEncounter.actualAt ?? pendingEncounter.scheduledAt) >=
      decisionTime;
  const latestIsBlocking =
    latestActiveDecision !== null &&
    BLOCKING_DECISIONS.has(latestActiveDecision.decision);

  const allows = (decision: CycleMonitoringProgressionDecision) =>
    !latestIsBlocking && latestActiveDecision?.decision === decision;

  let reviewState: CycleMonitoringDecisionReviewState = "none";
  if (latestIsBlocking) {
    reviewState = "not_proceeding";
  } else if (
    pendingAfterDecision ||
    latestActiveDecision?.decision === "awaiting_review"
  ) {
    reviewState = "awaiting_review";
  } else if (latestActiveDecision) {
    reviewState = "instruction_recorded";
  }

  return {
    reviewState,
    latestActiveDecision,
    latestPendingEncounter: pendingEncounter,
    allows,
  };
}

export function requiredProgressionDecisionForPlan(
  path: NonNullable<NonNullable<Cycle["cyclePlanSnapshot"]>>["path"],
): CycleMonitoringProgressionDecision | null {
  if (path === "oi_timed_intercourse") return "proceed_timed_intercourse";
  if (
    path === "natural_iui" ||
    path === "stimulated_iui" ||
    path === "oi_iui"
  ) {
    return "proceed_iui";
  }
  return null;
}

export function hasRecordedMonitoring(
  encounters: readonly CycleMonitoringEncounter[] | undefined,
): boolean {
  return (encounters ?? []).some(
    (encounter) =>
      encounter.status !== "voided" &&
      (encounter.actualAt !== null || encounter.measurements.length > 0),
  );
}
